import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Code,
  CopyButton,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  TagsInput,
  Title,
  Tooltip
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { useSupabase } from '@packages/supabase'
import { LoadingStatus } from '@packages/ui'
import {
  IconCheck,
  IconCopy,
  IconKey,
  IconPlus,
  IconTrash
} from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIntegrationCredentials } from '../hooks/useIntegrationCredentials'
import {
  canManageIntegrationKeys,
  canViewIntegrationKeys
} from '../lib/permissions'
import {
  DEFAULT_PUBLISHABLE_SCOPES,
  FORMS_INTEGRATION_SCOPES,
  type CreateCredentialResult,
  type FormsIntegrationScope,
  type IntegrationCredentialRow
} from '../types/integrationScopes'
import { TenantIntegrationsHowTo } from './TenantIntegrationsHowTo'

const SCOPE_LABEL_SUFFIX: Record<FormsIntegrationScope, string> = {
  'forms.schemas:read': 'scope.schemas_read',
  'forms.submissions:read': 'scope.submissions_read',
  'forms.submissions:write': 'scope.submissions_write',
  'forms.submissions:delete': 'scope.submissions_delete',
  'forms.audit:read': 'scope.audit_read'
}

const SCOPE_DEFAULTS: Record<FormsIntegrationScope, string> = {
  'forms.schemas:read': 'Read form schemas',
  'forms.submissions:read': 'Read submissions',
  'forms.submissions:write': 'Create and update submissions',
  'forms.submissions:delete': 'Delete submissions',
  'forms.audit:read': 'Read audit trail'
}

export type TenantIntegrationsSettingsProps = {
  i18nNamespace?: string
  translationKeyPrefix?: string
  embedBasePath?: string
  showEmbedDocs?: boolean
  showHowTo?: boolean
  titleOrder?: 3 | 4 | 5
}

type IntegrationFeedback = {
  color: 'red' | 'green'
  title?: string
  message: string
}

export function TenantIntegrationsSettings({
  i18nNamespace = 'ai',
  translationKeyPrefix = 'forms.integrations',
  embedBasePath = '/embed/forms',
  showEmbedDocs = true,
  showHowTo = true,
  titleOrder = 4
}: TenantIntegrationsSettingsProps) {
  const { t } = useTranslation(i18nNamespace)
  const { p, apiURL } = useSupabase()
  const manage = canManageIntegrationKeys(p)
  const view = canViewIntegrationKeys(p)
  const intl = (key: string, opts?: Record<string, unknown>) =>
    t(`${translationKeyPrefix}.${key}`, opts)
  const enabled = view

  const {
    credentials,
    credentialsLoading,
    credentialsError,
    logs,
    logsLoading,
    createCredential,
    revokeCredential
  } = useIntegrationCredentials(enabled)

  const [createOpen, setCreateOpen] = useState(false)
  const [createdKey, setCreatedKey] = useState<CreateCredentialResult | null>(
    null
  )
  const [creating, setCreating] = useState(false)

  const [name, setName] = useState('')
  const [credentialType, setCredentialType] = useState<'publishable' | 'secret'>(
    'publishable'
  )
  const [environment, setEnvironment] = useState<'live' | 'test'>('test')
  const [origins, setOrigins] = useState<string[]>(['http://localhost:3011'])
  const [selectedScopes, setSelectedScopes] = useState<string[]>([
    ...DEFAULT_PUBLISHABLE_SCOPES
  ])
  const [feedback, setFeedback] = useState<IntegrationFeedback | null>(null)

  const activeCredentials = useMemo(
    () => credentials.filter((c) => !c.revoked_at),
    [credentials]
  )

  const embedBase =
    typeof window !== 'undefined'
      ? `${window.location.origin}${embedBasePath}`
      : embedBasePath
  const gatewayUrl = `${apiURL.replace(/\/$/, '')}/functions/v1/integration_gateway`

  const resetCreateForm = () => {
    setName('')
    setCredentialType('publishable')
    setEnvironment('test')
    setOrigins(['http://localhost:3011'])
    setSelectedScopes([...DEFAULT_PUBLISHABLE_SCOPES])
  }

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    )
  }

  const handleCreate = async () => {
    if (!name.trim() || selectedScopes.length === 0) return
    setCreating(true)
    setFeedback(null)
    try {
      const result = await createCredential({
        name,
        credentialType,
        scopes: selectedScopes,
        allowedOrigins: credentialType === 'publishable' ? origins : [],
        environment
      })
      setCreateOpen(false)
      setCreatedKey(result)
      resetCreateForm()
    } catch (err) {
      setFeedback({
        color: 'red',
        title: intl('create_error_title', {
          defaultValue: 'Could not create API key'
        }),
        message:
          err instanceof Error
            ? err.message
            : intl('create_error_body', {
                defaultValue: 'Check permissions and try again.'
              })
      })
    } finally {
      setCreating(false)
    }
  }

  const confirmRevoke = (row: IntegrationCredentialRow) => {
    modals.openConfirmModal({
      title: intl('revoke_title', {
        defaultValue: 'Revoke API key'
      }),
      children: (
        <Text size="sm">
          {intl('revoke_confirm', {
            defaultValue:
              'Revoke “{{name}}” ({{prefix}}…)? Partners using this key will lose access.',
            name: row.name,
            prefix: row.key_prefix.slice(0, 12)
          })}
        </Text>
      ),
      labels: {
        confirm: intl('revoke', { defaultValue: 'Revoke' }),
        cancel: t('cancel', { defaultValue: 'Cancel' })
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await revokeCredential(row.id)
          setFeedback({
            color: 'green',
            message: intl('revoked', {
              defaultValue: 'API key revoked'
            })
          })
        } catch (err) {
          setFeedback({
            color: 'red',
            message:
              err instanceof Error ? err.message : 'Revoke failed'
          })
        }
      }
    })
  }

  if (!view) {
    return (
      <Alert color="gray">
        {intl('no_permission', {
          defaultValue:
            'You need integrations.read, integrations.manage, or forms.embed to manage API keys.'
        })}
      </Alert>
    )
  }

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Title order={titleOrder}>
          {intl('title', {
            defaultValue: 'API & integrations'
          })}
        </Title>
        <Text size="sm" c="dimmed">
          {intl('intro', {
            defaultValue:
              'Publishable keys exchange for short-lived tokens at your edge endpoint, then load embed routes in partner sites. Secrets are for server-side automation only.'
          })}
        </Text>
      </Stack>

      {showHowTo && (
        <TenantIntegrationsHowTo
          i18nNamespace={i18nNamespace}
          translationKeyPrefix={translationKeyPrefix}
          embedBasePath={embedBasePath}
        />
      )}

      {credentialsError && (
        <Alert color="red">
          {credentialsError instanceof Error
            ? credentialsError.message
            : String(credentialsError)}
        </Alert>
      )}

      {feedback && (
        <Alert
          color={feedback.color}
          title={feedback.title}
          withCloseButton
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconKey size={18} />
            <Text fw={600}>
              {intl('keys_heading', {
                defaultValue: 'API keys'
              })}
            </Text>
          </Group>
          {manage && (
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                resetCreateForm()
                setCreateOpen(true)
              }}
            >
              {intl('create_key', {
                defaultValue: 'Create key'
              })}
            </Button>
          )}
        </Group>

        {credentialsLoading ? (
          <LoadingStatus>
            {intl('loading_keys', {
              defaultValue: 'Loading API keys'
            })}
          </LoadingStatus>
        ) : activeCredentials.length === 0 ? (
          <Text size="sm" c="dimmed">
            {intl('no_keys', {
              defaultValue: 'No active API keys yet.'
            })}
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  {intl('col.name', {
                    defaultValue: 'Name'
                  })}
                </Table.Th>
                <Table.Th>
                  {intl('col.prefix', {
                    defaultValue: 'Prefix'
                  })}
                </Table.Th>
                <Table.Th>
                  {intl('col.type', {
                    defaultValue: 'Type'
                  })}
                </Table.Th>
                <Table.Th>
                  {intl('col.scopes', {
                    defaultValue: 'Scopes'
                  })}
                </Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {credentials.map((row) => (
                <Table.Tr
                  key={row.id}
                  opacity={row.revoked_at ? 0.5 : 1}
                >
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {row.name}
                    </Text>
                    {row.revoked_at && (
                      <Badge size="xs" color="gray" variant="light">
                        {intl('revoked_badge', {
                          defaultValue: 'Revoked'
                        })}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Code>{row.key_prefix}…</Code>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Badge size="xs" variant="outline">
                        {row.credential_type}
                      </Badge>
                      <Badge size="xs" color={row.environment === 'test' ? 'yellow' : 'teal'}>
                        {row.environment}
                      </Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" lineClamp={2}>
                      {(row.scopes ?? []).join(', ')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {manage && !row.revoked_at && (
                      <Tooltip
                        label={intl('revoke', {
                          defaultValue: 'Revoke'
                        })}
                      >
                        <Button
                          variant="subtle"
                          color="red"
                          size="xs"
                          onClick={() => confirmRevoke(row)}
                        >
                          <IconTrash size={14} />
                        </Button>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {showEmbedDocs ? (
        <Paper withBorder p="md" radius="md">
          <Text fw={600} mb="xs">
            {intl('embed_paths', {
              defaultValue: 'Embed paths'
            })}
          </Text>
          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              {intl('exchange_hint', {
                defaultValue:
                  'Exchange: POST {{url}}/functions/v1/integration_exchange with JSON { "key": "pk_…" }',
                url: apiURL.replace(/\/$/, '')
              })}
            </Text>
            <Code block>{embedBase}</Code>
            <Code block>
              {embedBase.replace(/\/$/, '')}/submissions
            </Code>
            <Text size="xs" c="dimmed" mt="xs">
              {intl('sdk_script', {
                defaultValue: 'Partner SDK: {{url}}/embed/v1/forms.js',
                url:
                  typeof window !== 'undefined'
                    ? window.location.origin
                    : 'https://app.stepcare.app'
              })}
            </Text>
            <Code block>{`<script src="${typeof window !== 'undefined' ? window.location.origin : ''}/embed/v1/forms.js" async></script>`}</Code>
          </Stack>
        </Paper>
      ) : (
        <Paper withBorder p="md" radius="md">
          <Text fw={600} mb="xs">
            {intl('gateway_heading', {
              defaultValue: 'JSON API (integration_gateway)'
            })}
          </Text>
          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              {intl('gateway_intro', {
                defaultValue:
                  'After exchange, POST with access_token and an operation. Scopes on each key limit what partners can call.'
              })}
            </Text>
            <Code block>{gatewayUrl}</Code>
            <Code block>
              {JSON.stringify(
                {
                  access_token: 'scist_…',
                  operation: 'forms.catalog.list',
                  params: { parent_id: null }
                },
                null,
                2
              )}
            </Code>
            <Text size="xs" c="dimmed">
              {intl('gateway_ops', {
                defaultValue:
                  'Operations: forms.catalog.list (params: parent_id), forms.page.get (params: page_id), forms.submissions.list (params: limit, subject_id), forms.submissions.save (params.payload), forms.voice.start (params: page_id).'
              })}
            </Text>
          </Stack>
        </Paper>
      )}

      <Paper withBorder p="md" radius="md">
        <Text fw={600} mb="md">
          {intl('logs_heading', {
            defaultValue: 'Recent API activity'
          })}
        </Text>
        {logsLoading ? (
          <LoadingStatus>
            {intl('loading_logs', {
              defaultValue: 'Loading activity'
            })}
          </LoadingStatus>
        ) : logs.length === 0 ? (
          <Text size="sm" c="dimmed">
            {intl('no_logs', {
              defaultValue: 'No integration requests logged yet.'
            })}
          </Text>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  {intl('log.time', {
                    defaultValue: 'Time'
                  })}
                </Table.Th>
                <Table.Th>
                  {intl('log.operation', {
                    defaultValue: 'Operation'
                  })}
                </Table.Th>
                <Table.Th>
                  {intl('log.status', {
                    defaultValue: 'Status'
                  })}
                </Table.Th>
                <Table.Th>
                  {intl('log.ms', {
                    defaultValue: 'ms'
                  })}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {logs.map((log) => (
                <Table.Tr key={log.id}>
                  <Table.Td>
                    <Text size="xs">
                      {new Date(log.created_at).toLocaleString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{log.operation}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="xs"
                      color={log.success ? 'green' : 'red'}
                    >
                      {log.status_code ?? (log.success ? 'OK' : 'ERR')}
                    </Badge>
                    {log.error_code && (
                      <Text size="xs" c="dimmed" ml={4} span>
                        {log.error_code}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{log.duration_ms ?? '—'}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title={intl('create_modal_title', {
          defaultValue: 'Create API key'
        })}
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label={intl('field.name', {
              defaultValue: 'Name'
            })}
            placeholder={intl('field.name_ph', {
              defaultValue: 'Acme portal embed'
            })}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />
          <Select
            label={intl('field.type', {
              defaultValue: 'Key type'
            })}
            value={credentialType}
            onChange={(v) =>
              setCredentialType((v as 'publishable' | 'secret') ?? 'publishable')
            }
            data={[
              {
                value: 'publishable',
                label: intl('type.publishable', {
                  defaultValue: 'Publishable (browser / iframe)'
                })
              },
              {
                value: 'secret',
                label: intl('type.secret', {
                  defaultValue: 'Secret (server only)'
                })
              }
            ]}
          />
          <Select
            label={intl('field.environment', {
              defaultValue: 'Environment'
            })}
            value={environment}
            onChange={(v) =>
              setEnvironment((v as 'live' | 'test') ?? 'test')
            }
            data={[
              { value: 'test', label: 'test' },
              { value: 'live', label: 'live' }
            ]}
          />
          {credentialType === 'publishable' && (
            <TagsInput
              label={intl('field.origins', {
                defaultValue: 'Allowed origins'
              })}
              description={intl('field.origins_hint', {
                defaultValue:
                  'Exact origins allowed to call integration_exchange (e.g. partner portal URL).'
              })}
              value={origins}
              onChange={setOrigins}
              placeholder="https://partner.example.com"
            />
          )}
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {intl('field.scopes', {
                defaultValue: 'Scopes'
              })}
            </Text>
            {FORMS_INTEGRATION_SCOPES.map((scope) => (
              <Checkbox
                key={scope}
                label={intl(SCOPE_LABEL_SUFFIX[scope], {
                  defaultValue: SCOPE_DEFAULTS[scope]
                })}
                checked={selectedScopes.includes(scope)}
                onChange={() => toggleScope(scope)}
              />
            ))}
          </Stack>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreateOpen(false)}>
              {t('cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              loading={creating}
              disabled={!name.trim() || selectedScopes.length === 0}
              onClick={() => void handleCreate()}
            >
              {intl('create_key', {
                defaultValue: 'Create key'
              })}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!createdKey}
        onClose={() => setCreatedKey(null)}
        title={intl('key_created_title', {
          defaultValue: 'Save your API key'
        })}
        size="lg"
      >
        {createdKey && (
          <Stack gap="md">
            <Alert color="yellow">
              {intl('key_once', {
                defaultValue:
                  'This key is shown only once. Copy it now and store it in your partner secret manager.'
              })}
            </Alert>
            <Group gap="xs" wrap="nowrap">
              <Code style={{ flex: 1 }} block>
                {createdKey.key}
              </Code>
              <CopyButton value={createdKey.key}>
                {({ copied, copy }) => (
                  <Button
                    color={copied ? 'teal' : 'gray'}
                    variant="light"
                    onClick={copy}
                    leftSection={
                      copied ? (
                        <IconCheck size={14} />
                      ) : (
                        <IconCopy size={14} />
                      )
                    }
                  >
                    {copied
                      ? intl('copied', {
                          defaultValue: 'Copied'
                        })
                      : intl('copy', {
                          defaultValue: 'Copy'
                        })}
                  </Button>
                )}
              </CopyButton>
            </Group>
            <Button onClick={() => setCreatedKey(null)}>
              {intl('done', { defaultValue: 'Done' })}
            </Button>
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}
